<template>
  <div class="overview-tab">
    <div class="stats-grid">
      <div class="stat-card votes-card">
        <div class="card-icon">
          <b-icon icon="vote" size="is-large"></b-icon>
        </div>
        <div class="card-content">
          <h3>{{ dashboardData.votes || 0 }}</h3>
          <p>Total Votes</p>
        </div>
      </div>

      <div class="stat-card reward-card" v-if="dashboardData.reward && !isPercentageReward">
        <div class="card-icon">
          <b-icon icon="gift" size="is-large"></b-icon>
        </div>
        <div class="card-content">
          <h3>{{ dashboardData.reward.reward }} {{ dashboardData.reward.currency }}</h3>
          <p>Reward Earned</p>
          <small>{{ dashboardData.reward.votes_required }} votes required</small>
        </div>
      </div>

      <div class="stat-card no-reward-card" v-else-if="!isPercentageReward">
        <div class="card-icon">
          <b-icon icon="trophy" size="is-large"></b-icon>
        </div>
        <div class="card-content">
          <h3>Keep Voting!</h3>
          <p>No reward yet</p>
          <small>Reach a reward tier to earn prizes</small>
        </div>
      </div>

      <div class="stat-card percentage-reward-card" v-if="isPercentageReward">
        <div class="card-icon">
          <b-icon icon="chart-line" size="is-large"></b-icon>
        </div>
        <div class="card-content">
          <h3>{{ percentageReward.percentage }}%</h3>
          <p>Earnings Rate</p>
          <small>Earn on every vote</small>
        </div>
      </div>

      <div class="stat-card due-reward-card" v-if="hasExpectedReward">
        <div class="card-icon">
          <b-icon icon="cash-multiple" size="is-large"></b-icon>
        </div>
        <div class="card-content">
          <h3>{{ $formatNumber(expectedRewardAmount) }} {{ rewardCurrency }}</h3>
          <p>Reward Amount Due</p>
          <small v-if="isPercentageReward && expectedReward.percentage">
            {{ expectedReward.percentage }}% of net vote payment per paid vote
          </small>
          <small v-else-if="expectedReward.remaining_amount != null && expectedReward.remaining_amount > 0">
            {{ $formatNumber(expectedReward.remaining_amount) }} {{ rewardCurrency }} remaining to be paid
          </small>
          <small v-else-if="expectedReward.paid_amount > 0">
            {{ $formatNumber(expectedReward.paid_amount) }} {{ rewardCurrency }} already paid
          </small>
        </div>
      </div>
    </div>

    <div class="details-section" v-if="participant.bio || participant.phone">
      <h2>Participant Details</h2>
      <div class="details-grid">
        <div class="detail-item" v-if="participant.bio">
          <b-icon icon="account"></b-icon>
          <div>
            <strong>Bio:</strong>
            <p>{{ participant.bio }}</p>
          </div>
        </div>
        <div class="detail-item" v-if="participant.phone">
          <b-icon icon="phone"></b-icon>
          <div>
            <strong>Phone:</strong>
            <p>{{ participant.phone }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="reward-info-section" v-if="(dashboardData.reward && !isPercentageReward) || hasExpectedReward">
      <h2>Reward Information</h2>
      <div class="reward-details">
        <div class="reward-item" v-if="hasExpectedReward">
          <div class="reward-label">Total Reward Due:</div>
          <div class="reward-value">{{ $formatNumber(expectedRewardAmount) }} {{ rewardCurrency }}</div>
        </div>
        <div class="reward-item" v-if="expectedReward && expectedReward.remaining_amount != null">
          <div class="reward-label">Remaining to Pay:</div>
          <div class="reward-value">{{ $formatNumber(expectedReward.remaining_amount) }} {{ rewardCurrency }}</div>
        </div>
        <div class="reward-item" v-if="expectedReward && expectedReward.paid_amount > 0">
          <div class="reward-label">Already Paid:</div>
          <div class="reward-value">{{ $formatNumber(expectedReward.paid_amount) }} {{ rewardCurrency }}</div>
        </div>
        <div class="reward-item" v-if="dashboardData.reward">
          <div class="reward-label">Votes Required:</div>
          <div class="reward-value">{{ dashboardData.reward.votes_required }}</div>
        </div>
        <div class="reward-item" v-if="dashboardData.reward">
          <div class="reward-label">Tier Reward:</div>
          <div class="reward-value">{{ dashboardData.reward.reward }} {{ dashboardData.reward.currency }}</div>
        </div>
        <div class="reward-item">
          <div class="reward-label">Your Votes:</div>
          <div class="reward-value">{{ dashboardData.votes || 0 }}</div>
        </div>
      </div>
    </div>

    <div class="percentage-reward-report" v-if="isPercentageReward">
      <h2>Earnings Report</h2>
      <div class="earnings-summary">
        <div class="summary-card highlight-due">
          <div class="summary-icon">
            <b-icon icon="cash-multiple" size="is-large"></b-icon>
          </div>
          <div class="summary-content">
            <h3>{{ $formatNumber(expectedRewardAmount) }} {{ rewardCurrency }}</h3>
            <p>Total Reward Due</p>
            <small v-if="expectedReward && expectedReward.percentage">{{ expectedReward.percentage }}% earnings rate</small>
          </div>
        </div>
        <div class="summary-card">
          <div class="summary-icon">
            <b-icon icon="cash" size="is-large"></b-icon>
          </div>
          <div class="summary-content">
            <h3>{{ $formatNumber(currentEarnings) }} RWF</h3>
            <p>Current Earnings</p>
          </div>
        </div>
        <div class="summary-card">
          <div class="summary-icon">
            <b-icon icon="trending-up" size="is-large"></b-icon>
          </div>
          <div class="summary-content">
            <h3>{{ $formatNumber(projectedEarnings) }} RWF</h3>
            <p>Projected Earnings</p>
          </div>
        </div>
        <div class="summary-card">
          <div class="summary-icon">
            <b-icon icon="wallet" size="is-large"></b-icon>
          </div>
          <div class="summary-content">
            <h3>{{ $formatNumber(totalEarnings) }} RWF</h3>
            <p>Total Earnings</p>
          </div>
        </div>
      </div>

      <div class="earnings-breakdown">
        <h3>Earnings Breakdown</h3>
        <div class="breakdown-table">
          <div class="table-header">
            <div class="table-cell">Period</div>
            <div class="table-cell">Votes</div>
            <div class="table-cell">Revenue</div>
            <div class="table-cell">Your Share</div>
          </div>
          <div v-for="earning in earningsData" :key="earning.period" class="table-row">
            <div class="table-cell" data-label="Period">{{ earning.period }}</div>
            <div class="table-cell" data-label="Votes">{{ earning.votes }}</div>
            <div class="table-cell" data-label="Revenue">{{ earning.revenue }} RWF</div>
            <div class="table-cell" data-label="Your Share">{{ earning.share }} RWF</div>
          </div>
        </div>
      </div>

      <div class="performance-tips">
        <h3>Performance Tips</h3>
        <ul>
          <li v-if="isPercentageReward">More votes = Higher earnings! Each vote generates {{ percentageReward.percentage }}% commission</li>
          <li>Promote your participation to increase visibility and votes</li>
          <li>Engage with your supporters to encourage more voting</li>
          <li>Share your unique participant code: #{{ participant.item_index }}</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OverviewTab',
  props: {
    participant: { type: Object, default: () => ({}) },
    dashboardData: { type: Object, default: () => ({ votes: 0, reward: null }) },
    percentageReward: { type: Object, default: null },
    expectedReward: { type: Object, default: null },
    earningsData: { type: Array, default: () => [] },
    isPercentageReward: { type: Boolean, default: false },
    currentEarnings: { type: Number, default: 0 },
    projectedEarnings: { type: Number, default: 0 },
    totalEarnings: { type: Number, default: 0 },
  },
  computed: {
    hasExpectedReward() {
      return this.expectedReward && this.expectedReward.reward_type
    },
    expectedRewardAmount() {
      if (!this.expectedReward) return 0
      return Number(this.expectedReward.amount) || 0
    },
    rewardCurrency() {
      if (!this.expectedReward || !this.expectedReward.currency) return 'RWF'
      return this.expectedReward.currency
    },
  },
}
</script>

<style lang="scss" scoped>
.overview-tab {
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 20px;
    margin-bottom: 40px;

    .stat-card {
      background: #fff;
      border-radius: 4px;
      padding: 20px;
      border: 1px solid #ddd;
      display: flex;
      align-items: center;
      gap: 16px;

      .card-icon { color: var(--wecodefy-secondary-color, #9fc953); }
      .card-content {
        flex: 1;
        h3 { margin: 0; font-size: 28px; font-weight: 700; color: #1a191b; }
        p { margin: 5px 0 0; color: #797979; font-size: 13px; text-transform: uppercase; letter-spacing: 0.02em; }
        small { color: #797979; font-size: 12px; }
      }
      &.reward-card { .card-icon, h3 { color: #1a191b; } }
      &.no-reward-card { .card-icon, h3 { color: #797979; } }
      &.percentage-reward-card { .card-icon, h3 { color: #1a191b; } }
      &.due-reward-card {
        border-color: var(--wecodefy-secondary-color, #9fc953);
        .card-icon, h3 { color: #1a191b; }
      }
    }
  }

  .details-section, .reward-info-section, .percentage-reward-report {
    background: #fff;
    border-radius: 4px;
    padding: 20px;
    margin-bottom: 16px;
    border: 1px solid #ddd;
    h2 { margin: 0 0 16px; color: #1a191b; font-size: 20px; font-weight: 600; }
    h3 { margin: 16px 0 12px; color: #1a191b; font-size: 16px; font-weight: 600; }
  }

  .details-grid {
    .detail-item {
      display: flex; align-items: flex-start; gap: 15px;
      .icon { color: var(--wecodefy-secondary-color, #9fc953); margin-top: 2px; }
      strong { color: #1a191b; display: block; margin-bottom: 5px; }
      p { margin: 0; color: #797979; line-height: 1.5; }
    }
  }

  .reward-details .reward-item {
    display: flex; justify-content: space-between; align-items: center;
    padding: 12px 0; border-bottom: 1px solid #eee;
    &:last-child { border-bottom: none; }
    .reward-label { font-weight: 500; color: #797979; font-size: 13px; }
    .reward-value { font-weight: 600; color: #1a191b; }
  }

  .percentage-reward-report {
    .earnings-summary {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 10px;
      margin-bottom: 20px;
      .summary-card {
        background: #fff;
        border: 1px solid #ddd;
        border-radius: 4px;
        padding: 16px;
        display: flex;
        align-items: center;
        gap: 16px;
        .summary-content {
          h3 { margin: 0; font-size: 22px; font-weight: 700; color: #1a191b; }
          p { margin: 5px 0 0; color: #797979; font-size: 12px; text-transform: uppercase; }
          small { display: block; margin-top: 6px; color: #797979; font-size: 12px; }
        }
        &.highlight-due {
          border-color: var(--wecodefy-secondary-color, #9fc953);
          border-left-width: 4px;
        }
      }
    }
    .breakdown-table {
      border: 1px solid #e1e5e9; border-radius: 8px; overflow: hidden; margin-top: 15px;
      .table-header, .table-row {
        display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 1px; background: #f8f9fa;
        .table-cell { padding: 12px 15px; background: white; text-align: center; font-weight: 500; color: #666; }
      }
      .table-header .table-cell { font-weight: 600; color: #333; background: #f1f3f4; }
      .table-row:hover .table-cell { background: #f8f9fa; }
    }
    .performance-tips {
      background: #fafafa; border: 1px solid #eee; border-radius: 4px; padding: 16px; margin-top: 16px;
      h3 { margin: 0 0 12px; color: #1a191b; }
      ul { margin: 0; padding-left: 20px; li { margin-bottom: 8px; color: #555; line-height: 1.5; } }
    }
  }
}

@media (max-width: 768px) {
  .overview-tab {
    .stats-grid { grid-template-columns: 1fr; .stat-card { padding: 20px; flex-direction: column; text-align: center; } }
    .percentage-reward-report .breakdown-table {
      .table-header { display: none; }
      .table-row { grid-template-columns: 1fr; .table-cell { text-align: left; border-bottom: 1px solid #eee;
        &:before { content: attr(data-label) ": "; font-weight: 600; color: #666; } } }
    }
  }
}
</style>
